import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import DisplayMap from '../../DisplayMap/index.tsx';
import { SearchModes, TravelTypes } from '../../../constants';

const Map = ({ tripId, onMarkerClick, hoveredMarkerKey, searchMode }) => {
  const isTripSearch = !tripId;
  const { searchResult, selectedSearchMode } = useSelector((store) => store.tripSearch);
  const trip = useSelector((state) => state.trip.selectedTrips[tripId]);
  const shownSearchMode = searchMode || selectedSearchMode;

  // Selecting the stored array and filtering separately keeps the reference stable, so
  // DisplayMap is not re-rendered by every unrelated action and the static map url it
  // derives does not churn.
  const pins = useSelector((state) => {
    if (shownSearchMode === SearchModes.TOUR) {
      return isTripSearch
        ? searchResult.tourPins
        : state.tour.tourSearches[tripId]?.responses?.tourPins;
    }
    if (shownSearchMode !== SearchModes.HOTEL) {
      return searchResult.markers;
    }
    if (isTripSearch) {
      return searchResult.hotelPins;
    }
    const hotelSearchState = state.hotel.hotelSearches[tripId];
    return state.hotel.hotelSearchResponses[hotelSearchState.queryString].hotelPins;
  });

  const markers = useMemo(() => (pins || []).filter((pin) => pin.cost !== 0), [pins]);

  let selectedMarkerKey;
  if (trip) {
    if (
      shownSearchMode === SearchModes.HOTEL &&
      trip.hotelItineraries &&
      Object.keys(trip.hotelItineraries).length > 0 &&
      trip.hotelItineraries[0] &&
      trip.hotelItineraries[0].hotelKey
    ) {
      selectedMarkerKey = trip.hotelItineraries[0].hotelKey;
    }
    if (
      shownSearchMode === SearchModes.CUSTOM &&
      trip.customInventoryItinerary &&
      trip.customInventoryItinerary.uniqueKey
    ) {
      selectedMarkerKey = trip.customInventoryItinerary.uniqueKey;
    }
    if (
      shownSearchMode === SearchModes.TOUR &&
      trip.tourItineraries &&
      trip.tourItineraries.length > 0 &&
      trip.tourItineraries[0] &&
      trip.tourItineraries[0].productId
    ) {
      selectedMarkerKey = trip.tourItineraries[0].productId;
    }
  }
  return (
    <DisplayMap
      markers={markers}
      hoveredMarkerKey={hoveredMarkerKey}
      tripId={tripId}
      onMarkerClick={onMarkerClick}
      selectedMarkerKey={selectedMarkerKey}
      // Pins and tooltips differ per travel type: a hotel shows a nightly rate, a tour a single price.
      mode={shownSearchMode === SearchModes.TOUR ? TravelTypes.tour : TravelTypes.hotel}
    />
  );
};

Map.defaultProps = {
  hoveredMarkerKey: null,
  tripId: undefined,
  searchMode: undefined,
};

Map.propTypes = {
  tripId: PropTypes.string,
  onMarkerClick: PropTypes.func.isRequired,
  hoveredMarkerKey: PropTypes.string,
  searchMode: PropTypes.string,
};

export default memo(Map);
